import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";

import classicAlertSound from "../../assets/sound.mp3";

const NotificationSoundContext = createContext();

const STORAGE_KEY = "whaticket-notification-sound";
const VALID_TONES = ["soft", "discreet", "classic"];

export const DEFAULT_NOTIFICATION_SOUND_SETTINGS = {
  enabled: true,
  tone: "soft",
  volume: 25,
  cooldownSeconds: 8,
};

const clamp = (value, minimum, maximum) =>
  Math.min(maximum, Math.max(minimum, value));

const normalizeSettings = (settings = {}) => ({
  enabled:
    typeof settings.enabled === "boolean"
      ? settings.enabled
      : DEFAULT_NOTIFICATION_SOUND_SETTINGS.enabled,
  tone: VALID_TONES.includes(settings.tone)
    ? settings.tone
    : DEFAULT_NOTIFICATION_SOUND_SETTINGS.tone,
  volume: clamp(
    Number.isFinite(Number(settings.volume))
      ? Number(settings.volume)
      : DEFAULT_NOTIFICATION_SOUND_SETTINGS.volume,
    0,
    100
  ),
  cooldownSeconds: clamp(
    Number.isFinite(Number(settings.cooldownSeconds))
      ? Number(settings.cooldownSeconds)
      : DEFAULT_NOTIFICATION_SOUND_SETTINGS.cooldownSeconds,
    0,
    60
  ),
});

const readStoredSettings = () => {
  try {
    const storedSettings = localStorage.getItem(STORAGE_KEY);
    return storedSettings
      ? normalizeSettings(JSON.parse(storedSettings))
      : DEFAULT_NOTIFICATION_SOUND_SETTINGS;
  } catch (error) {
    console.warn("Could not read notification sound preferences", error);
    return DEFAULT_NOTIFICATION_SOUND_SETTINGS;
  }
};

const playClassicTone = volume => {
  const audio = new Audio(classicAlertSound);
  audio.volume = volume;
  return audio.play().catch(error => {
    console.warn("Could not play the classic notification sound", error);
  });
};

const generatedToneUrls = {};

const writeWaveString = (dataView, offset, value) => {
  for (let index = 0; index < value.length; index += 1) {
    dataView.setUint8(offset + index, value.charCodeAt(index));
  }
};

const createGeneratedToneUrl = tone => {
  if (generatedToneUrls[tone]) return generatedToneUrls[tone];

  const sampleRate = 44100;
  const notes =
    tone === "discreet"
      ? [{ frequency: 440, start: 0, duration: 0.16, amplitude: 0.72 }]
      : [
          { frequency: 523.25, start: 0, duration: 0.18, amplitude: 0.62 },
          { frequency: 659.25, start: 0.19, duration: 0.22, amplitude: 0.58 },
        ];
  const totalDuration = tone === "discreet" ? 0.22 : 0.46;
  const sampleCount = Math.ceil(sampleRate * totalDuration);
  const bytesPerSample = 2;
  const waveBuffer = new ArrayBuffer(44 + sampleCount * bytesPerSample);
  const dataView = new DataView(waveBuffer);

  writeWaveString(dataView, 0, "RIFF");
  dataView.setUint32(4, 36 + sampleCount * bytesPerSample, true);
  writeWaveString(dataView, 8, "WAVE");
  writeWaveString(dataView, 12, "fmt ");
  dataView.setUint32(16, 16, true);
  dataView.setUint16(20, 1, true);
  dataView.setUint16(22, 1, true);
  dataView.setUint32(24, sampleRate, true);
  dataView.setUint32(28, sampleRate * bytesPerSample, true);
  dataView.setUint16(32, bytesPerSample, true);
  dataView.setUint16(34, 16, true);
  writeWaveString(dataView, 36, "data");
  dataView.setUint32(40, sampleCount * bytesPerSample, true);

  for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex += 1) {
    const time = sampleIndex / sampleRate;
    let sampleValue = 0;

    notes.forEach(note => {
      const localTime = time - note.start;
      if (localTime < 0 || localTime > note.duration) return;

      const progress = localTime / note.duration;
      const envelope = Math.sin(Math.PI * progress) ** 2;
      sampleValue +=
        Math.sin(2 * Math.PI * note.frequency * localTime) *
        note.amplitude *
        envelope;
    });

    const limitedSample = clamp(sampleValue, -1, 1);
    dataView.setInt16(
      44 + sampleIndex * bytesPerSample,
      Math.round(limitedSample * 32767),
      true
    );
  }

  const waveBlob = new Blob([waveBuffer], { type: "audio/wav" });
  generatedToneUrls[tone] = URL.createObjectURL(waveBlob);
  return generatedToneUrls[tone];
};

const playGeneratedTone = (tone, volume) => {
  const audio = new Audio(createGeneratedToneUrl(tone));
  audio.volume = volume;
  return audio.play().catch(error => {
    console.warn(`Could not play the ${tone} notification sound`, error);
  });
};

const playTone = settings => {
  const volume = clamp(settings.volume, 0, 100) / 100;
  if (volume === 0) return Promise.resolve();

  if (settings.tone === "classic") {
    return playClassicTone(volume);
  }

  return playGeneratedTone(settings.tone, volume);
};

export const NotificationSoundProvider = ({ children }) => {
  const [settings, setSettings] = useState(readStoredSettings);
  const lastPlayedAtRef = useRef(0);

  const saveSettings = useCallback(nextSettings => {
    const normalizedSettings = normalizeSettings(nextSettings);
    setSettings(normalizedSettings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedSettings));
  }, []);

  const playNotificationSound = useCallback(() => {
    if (!settings.enabled) return false;

    const now = Date.now();
    const cooldownMilliseconds = settings.cooldownSeconds * 1000;
    if (now - lastPlayedAtRef.current < cooldownMilliseconds) return false;

    lastPlayedAtRef.current = now;
    playTone(settings);
    return true;
  }, [settings]);

  const previewNotificationSound = useCallback(previewSettings => {
    const normalizedSettings = normalizeSettings({
      ...previewSettings,
      enabled: true,
    });
    return playTone(normalizedSettings);
  }, []);

  const contextValue = useMemo(
    () => ({
      settings,
      saveSettings,
      playNotificationSound,
      previewNotificationSound,
    }),
    [
      settings,
      saveSettings,
      playNotificationSound,
      previewNotificationSound,
    ]
  );

  return (
    <NotificationSoundContext.Provider value={contextValue}>
      {children}
    </NotificationSoundContext.Provider>
  );
};

NotificationSoundProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useNotificationSound = () =>
  useContext(NotificationSoundContext);
